/**
 * DiscoveryScene — onboarding's "constellation of everything you have access
 * to" stage. Pixel-for-pixel port of /tmp/wayne-mocks/04-onboarding-discovery
 * with the orbital math driven by useOrbitalScene (see hook for the
 * architecture rationale).
 *
 * Stages:
 *   - dormant     — veil + "Begin discovery →" CTA over a darkened scene
 *   - igniting    — atmosphere fades in, tiles materialize on their orbits
 *   - live        — discovery runs; tiles flip running → succeeded as
 *                   connectors prop changes; beams arc toward Asha core
 *   - completing  — radius collapses to 0, core flares + flashes
 *   - welcomed    — overlay card replaces the scene with the welcome copy
 *
 * The component is "headless" in the sense that all sequencing happens
 * upstream — the parent owns the state machine and feeds connector states
 * + the current stage in. We only render what we are told to render plus
 * the per-frame orbital transform.
 *
 * Honors prefers-reduced-motion: skips rAF, freezes tiles on static base
 * positions, and disables CSS animations via DiscoveryScene.css.
 */
import {
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';

import { prefersReducedMotion } from '@/tokens/motion';

import {
  useOrbitalScene,
  type OrbitalTileConfig,
  type RingNumber,
} from './useOrbitalScene';

import './DiscoveryScene.css';

export type DiscoverySceneStage =
  | 'dormant'
  | 'igniting'
  | 'live'
  | 'completing'
  | 'welcomed';

export type ConnectorState = 'pending' | 'running' | 'succeeded' | 'failed';

export interface DiscoverySceneConnector {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly state: ConnectorState;
  readonly ring: RingNumber;
  readonly angleDeg: number;
}

export interface DiscoverySceneProps {
  readonly stage: DiscoverySceneStage;
  readonly connectors: ReadonlyArray<DiscoverySceneConnector>;
  readonly userName: string;
  readonly onIgnite?: (() => void) | undefined;
  readonly onTakeTour?: (() => void) | undefined;
  readonly onSkipTour?: (() => void) | undefined;
}

/**
 * The starfield is purely decorative; we mount it once on first render of
 * the scene pane. The stars are positioned with random %s so jsdom and
 * the browser both produce a stable layout.
 */
function useStars(reduced: boolean): ReadonlyArray<StarConfig> {
  return useMemo(() => {
    if (reduced) return [];
    const out: StarConfig[] = [];
    for (let i = 0; i < 90; i += 1) {
      const depth = Math.random();
      const size = 1 + Math.round(depth * 2);
      out.push({
        left: Number((Math.random() * 100).toFixed(2)),
        top: Number((Math.random() * 100).toFixed(2)),
        size,
        opacity: Number((0.18 + depth * 0.5).toFixed(2)),
        twinkleDuration: Number((5 + Math.random() * 6).toFixed(1)),
        delay: Number((Math.random() * -8).toFixed(1)),
      });
    }
    return out;
  }, [reduced]);
}

interface StarConfig {
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly opacity: number;
  readonly twinkleDuration: number;
  readonly delay: number;
}

const ACTIVE_STAGES: ReadonlySet<DiscoverySceneStage> = new Set([
  'igniting',
  'live',
  'completing',
]);

export default function DiscoveryScene({
  stage,
  connectors,
  userName,
  onIgnite,
  onTakeTour,
  onSkipTour,
}: DiscoverySceneProps): ReactElement {
  const reduced = prefersReducedMotion();
  const stars = useStars(reduced);

  // Prepare the tile config for the rAF hook. Stable identity per id so
  // the hook's runtime map can reuse entries across re-renders.
  const tileConfigs = useMemo<ReadonlyArray<OrbitalTileConfig>>(
    () =>
      connectors.map((c) => ({
        id: c.id,
        ring: c.ring,
        angleDeg: c.angleDeg,
      })),
    [connectors],
  );

  const { stageRef, tileRefFor } = useOrbitalScene({
    tiles: tileConfigs,
    collapsing: stage === 'completing',
  });

  // Beams: when a connector flips to "succeeded" we emit a transient
  // SVG path that arcs from its current screen position to Asha. We track
  // previous states so the effect only fires on the running→succeeded
  // edge, not on every re-render that happens while a tile is succeeded.
  const beamsSvgRef = useRef<SVGSVGElement | null>(null);
  const stageElRef = useRef<HTMLDivElement | null>(null);
  const coreElRef = useRef<HTMLDivElement | null>(null);
  const prevStatesRef = useRef<Map<string, ConnectorState>>(new Map());

  // Compose the stage ref so both useOrbitalScene and our local ref get the node.
  const composedStageRef = (node: HTMLDivElement | null): void => {
    stageElRef.current = node;
    stageRef(node);
  };

  useEffect(() => {
    if (reduced) {
      // Still keep the prev map current so we don't fire all beams at once
      // the next time someone toggles reduced motion off mid-session.
      const next = new Map<string, ConnectorState>();
      for (const c of connectors) next.set(c.id, c.state);
      prevStatesRef.current = next;
      return;
    }

    const stageEl = stageElRef.current;
    const coreEl = coreElRef.current;
    const beamsSvg = beamsSvgRef.current;
    if (!stageEl || !coreEl || !beamsSvg) return;

    const prev = prevStatesRef.current;
    for (const c of connectors) {
      const last = prev.get(c.id);
      if (c.state === 'succeeded' && last !== 'succeeded') {
        const tileEl = stageEl.querySelector<HTMLElement>(
          `[data-tile-id="${c.id}"]`,
        );
        if (tileEl) {
          shootBeam(tileEl, coreEl, stageEl, beamsSvg);
        }
      }
    }
    const next = new Map<string, ConnectorState>();
    for (const c of connectors) next.set(c.id, c.state);
    prevStatesRef.current = next;
  }, [connectors, reduced]);

  return (
    <section
      className="ds-pane"
      data-stage={stage}
      data-testid="discovery-scene"
      aria-label="Discovery scene"
    >
      <div className="ds-nebula" aria-hidden="true" />
      <div className="ds-stars" aria-hidden="true">
        {stars.map((s, i) => (
          <span
            // Stable index keys: the array is memoized for the lifetime of
            // the mount and is purely decorative.
            key={`star-${i}`}
            className="ds-star"
            style={
              {
                left: `${s.left}%`,
                top: `${s.top}%`,
                width: `${s.size}px`,
                height: `${s.size}px`,
                ['--o' as string]: s.opacity,
                ['--td' as string]: `${s.twinkleDuration}s`,
                ['--delay' as string]: `${s.delay}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
      <div className="ds-shafts" aria-hidden="true" />

      <div
        className="ds-stage-area"
        data-testid="discovery-stage-area"
        ref={composedStageRef}
      >
        <div className="ds-halo" aria-hidden="true" />
        <div className="ds-ring r1" aria-hidden="true" />
        <div className="ds-ring r2" aria-hidden="true" />
        <div className="ds-ring r3" aria-hidden="true" />

        {connectors.map((c) => (
          <div
            key={c.id}
            data-tile-id={c.id}
            data-state={ACTIVE_STAGES.has(stage) ? c.state : 'pending'}
            data-ring={c.ring}
            className="ds-tile"
            ref={tileRefFor(c.id)}
            role="img"
            aria-label={`${c.label} — ${c.state}`}
          >
            <div className="ds-tile-pin">
              <span className="ds-tile-glyph" aria-hidden="true">
                {c.icon}
              </span>
              <span className="ds-tile-label">{c.label}</span>
            </div>
          </div>
        ))}

        <svg className="ds-beams" ref={beamsSvgRef} aria-hidden="true" />

        <div className="ds-core" ref={coreElRef} data-testid="discovery-core">
          <span className="ds-core-name">Asha</span>
          <span className="ds-core-sub">Welcome home</span>
        </div>
      </div>

      <div className="ds-flash" aria-hidden="true" />

      {stage === 'welcomed' ? (
        <div className="ds-welcomed-card" data-testid="discovery-welcomed">
          <WelcomedInner
            userName={userName}
            connectors={connectors}
            {...(onTakeTour ? { onTakeTour } : {})}
            {...(onSkipTour ? { onSkipTour } : {})}
          />
        </div>
      ) : null}

      {stage === 'dormant' ? (
        <div className="ds-dormant-veil" data-testid="discovery-dormant">
          <DormantInner {...(onIgnite ? { onIgnite } : {})} />
        </div>
      ) : null}
    </section>
  );
}

interface DormantInnerProps {
  readonly onIgnite?: (() => void) | undefined;
}

function DormantInner({
  onIgnite,
}: DormantInnerProps): ReactElement {
  return (
    <div
      className="relative max-w-[720px] w-full px-8 flex flex-col items-center text-center"
    >
      <div className="text-[10.5px] tracking-[0.22em] text-wayne-ink-3 uppercase font-bold mb-[18px]">
        Welcome · Onboarding
      </div>
      <h1
        className="font-serif text-[clamp(72px,9vw,124px)] font-medium leading-[1.05] mb-[18px] text-wayne-ink inline-flex items-baseline justify-center gap-[0.32em] pb-[0.08em]"
        style={{ letterSpacing: '-0.03em' }}
      >
        Wayne
        <em className="italic text-wayne-accent font-medium">AI</em>
      </h1>
      <div className="text-[12px] font-bold tracking-[0.32em] uppercase text-wayne-ink-2 mb-10">
        Agents who <span className="text-wayne-accent">already</span> know you
      </div>
      <h2
        className="font-serif text-[22px] font-medium italic leading-[1.35] text-wayne-ink-2 max-w-[580px] mb-4"
      >
        A constellation of{' '}
        <em className="not-italic italic text-wayne-accent font-medium">
          everything
        </em>{' '}
        you have access to.
      </h2>
      <p className="text-[14.5px] leading-[1.6] text-wayne-ink-2 max-w-[600px] mb-8">
        Wayne pulls together your phonetool, bindles, AWS accounts, Taskei
        rooms, CTIs, oncall rotations, channels you live in, and a dozen
        other sources — so the agents have your context from minute one.
        Nothing leaves Amazon.
      </p>
      <button
        type="button"
        onClick={onIgnite}
        className="appearance-none bg-wayne-accent text-wayne-bg border-0 rounded-full px-8 py-4 text-[14px] font-bold cursor-pointer tracking-[0.04em] hover:-translate-y-px transition-transform"
        style={{
          boxShadow:
            '0 10px 28px -6px color-mix(in srgb, var(--wayne-accent) 55%, transparent)',
        }}
        data-testid="discovery-begin-button"
      >
        Begin discovery →
      </button>
      <div className="mt-4 text-[11px] text-wayne-ink-3 tracking-[0.04em]">
        Takes about 18 seconds. You can re-run any time.
      </div>
    </div>
  );
}

interface WelcomedInnerProps {
  readonly userName: string;
  readonly connectors: ReadonlyArray<DiscoverySceneConnector>;
  readonly onTakeTour?: (() => void) | undefined;
  readonly onSkipTour?: (() => void) | undefined;
}

function WelcomedInner({
  userName,
  connectors,
  onTakeTour,
  onSkipTour,
}: WelcomedInnerProps): ReactElement {
  const sources = connectors.length;
  const succeeded = connectors.filter((c) => c.state === 'succeeded').length;
  return (
    <div className="text-center max-w-[640px] px-8 flex flex-col items-center">
      <div className="text-[10.5px] tracking-[0.22em] text-wayne-ink-3 uppercase font-bold mb-[18px]">
        Discovery complete
      </div>
      <h1
        className="font-serif text-[clamp(56px,8vw,96px)] font-medium leading-none mb-[18px] text-wayne-ink"
        style={{ letterSpacing: '-0.02em' }}
      >
        Welcome home,{' '}
        <em className="not-italic italic text-wayne-accent font-medium">
          {userName}
        </em>
        .
      </h1>
      <p className="text-[16px] leading-[1.55] text-wayne-ink-2 mb-9 max-w-[520px]">
        Wayne has stitched together everything you have access to. The agents
        already know your stack, your tickets, your channels. Take a quick
        tour or jump straight in.
      </p>
      <div className="flex gap-8 mb-9">
        <Stat value={sources} label="Sources" />
        <Stat value={succeeded * 10 + 8} label="Items found" />
        <Stat value={9} label="Agents ready" />
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onTakeTour}
          className="appearance-none bg-wayne-accent text-wayne-bg border-0 rounded-full px-8 py-4 text-[14px] font-bold cursor-pointer tracking-[0.04em]"
          style={{
            boxShadow:
              '0 10px 28px -6px color-mix(in srgb, var(--wayne-accent) 55%, transparent)',
          }}
          data-testid="discovery-tour-button"
        >
          Take the tour →
        </button>
        <button
          type="button"
          onClick={onSkipTour}
          className="appearance-none bg-transparent text-wayne-ink-2 border border-wayne-hairline-2 rounded-full px-6 py-4 text-[14px] font-semibold cursor-pointer"
          data-testid="discovery-skip-tour-button"
        >
          Skip to home
        </button>
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }): ReactElement {
  return (
    <div className="flex flex-col items-center">
      <strong
        className="font-serif text-[28px] font-medium text-wayne-accent"
        style={{ fontFeatureSettings: "'tnum'" }}
      >
        {value}
      </strong>
      <span className="text-[11px] tracking-[0.12em] uppercase text-wayne-ink-3 mt-1">
        {label}
      </span>
    </div>
  );
}

/**
 * shootBeam — emit a transient SVG path that arcs from a tile to Asha.
 * Lives outside React so we don't have to choreograph element creation
 * via state. The path removes itself after the animation completes.
 */
function shootBeam(
  tileEl: HTMLElement,
  coreEl: HTMLElement,
  stageEl: HTMLElement,
  beamsSvg: SVGSVGElement,
): void {
  const sa = stageEl.getBoundingClientRect();
  const r = tileEl.getBoundingClientRect();
  const cr = coreEl.getBoundingClientRect();
  const x1 = r.left + r.width / 2 - sa.left;
  const y1 = r.top + r.height / 2 - sa.top;
  const x2 = cr.left + cr.width / 2 - sa.left;
  const y2 = cr.top + cr.height / 2 - sa.top;
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2 - 30;
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('class', 'ds-beam');
  path.setAttribute('d', `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`);
  const len = Math.hypot(x2 - x1, y2 - y1) + 60;
  path.setAttribute('stroke-dasharray', String(len));
  path.style.setProperty('--len', String(len));
  beamsSvg.appendChild(path);
  window.setTimeout(() => {
    path.remove();
  }, 1200);
}
